/**
 * Image utility functions for purchasing2
 * Handles image display, fallbacks, and S3 integration
 */

const API_BASE = process.env.REACT_APP_SERVER_URL || '';

/**
 * Get the full image URL for an item
 * @param {Object} item - Item object with image properties
 * @returns {string} Image URL or placeholder
 */
export function getItemImageUrl(item) {
  // Try different possible image URL properties
  const imageUrl = item.imageUrl || item.image_url || item.imageUri || item.thumbnailUrl || item.thumbnail_url;
  
  if (imageUrl && imageUrl !== null) {
    return imageUrl;
  }
  
  // Return a placeholder image
  return '/images/placeholder-item.svg';
}

/**
 * Get the thumbnail URL for an item
 * @param {Object} item - Item object with image properties
 * @returns {string} Thumbnail URL or main image URL
 */
export function getItemThumbnailUrl(item) {
  const thumbnailUrl = item.thumbnailUrl || item.thumbnail_url;
  const imageUrl = item.imageUrl || item.image_url || item.imageUri;
  
  if (thumbnailUrl && thumbnailUrl !== null) {
    return thumbnailUrl;
  }
  
  if (imageUrl && imageUrl !== null) {
    return imageUrl;
  }
  
  return '/images/placeholder-item.svg';
}

/**
 * Check if an image URL is valid
 * @param {string} url - Image URL to check
 * @returns {boolean} True if URL looks valid
 */
export function isValidImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  
  // Check if it's a placeholder
  if (url.includes('placeholder')) return true;
  
  // Check if it's a valid URL
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Upload an image to S3 via the API
 * @param {File} file - Image file to upload
 * @param {string} folder - S3 folder (default: 'inventory')
 * @param {string} filename - Optional custom filename
 * @returns {Promise<Object>} Upload result
 */
export async function uploadItemImage(file, folder = 'inventory', filename = null) {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('folder', folder);
  if (filename) {
    formData.append('filename', filename);
  }

  const response = await fetch(`${API_BASE}/api/v1/s3-images/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include', // Include cookies for authentication
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get image upload URL for direct S3 upload
 * @param {string} folder - S3 folder
 * @param {string} filename - Filename
 * @returns {Promise<string>} Upload URL
 */
export async function getImageUploadUrl(folder, filename) {
  const response = await fetch(`${API_BASE}/api/v1/s3-images/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ folder, filename }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get upload URL: ${response.statusText}`);
  }

  const data = await response.json();
  return data.uploadUrl;
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Validate image file
 * @param {File} file - File to validate
 * @returns {Object} Validation result
 */
export function validateImageFile(file) {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Only image files are allowed (JPEG, PNG, GIF, WebP)' };
  }
  
  return { valid: true };
}
